import React, { useCallback, useEffect, useState, useRef } from 'react';
import {
  Users, Building2, FileText, Workflow,
  Eye, Globe, Key, 
} from 'lucide-react';
import { API_BASE_URL, getHeaders } from '../utils/api';
import { readAdminApiErrorMessage } from '../utils/adminUi';

const CACHE_DURATION = 5 * 60 * 1000;
const FOLLOW_UP_REFRESH_DELAY = 6 * 1000;
const MAX_SURVEY_RECHECKS = 3;

const defaultStats = {
  users: { total: 0, active: 0 },
  organizations: { total: 0, active: 0 },
  contents: { total: 0, approved: 0 },
  userFlows: { total: 0, active: 0 },
  survey: { total: 0, response: 0 },
  visibility: { total: 0, approved: 0 },
  siteCollections: { total: 0, active: 0 },
  sessions: { loggedInUsersCurrent: 0 },
  apiKeys: { provisioned: 0, active: 0 },
};

function parseMetrics(metrics = {}, previousStats = defaultStats) {
  const getByPath = (obj, path) => {
    return String(path)
      .split('.')
      .reduce((current, part) => (current && typeof current === 'object' ? current[part] : undefined), obj);
  };

  const readMetric = (keys, fallback = 0) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (const key of keyList) {
      const directValue = metrics?.[key];
      const nestedValue = getByPath(metrics, key);
      const raw = directValue ?? nestedValue;
      if (raw !== null && raw !== undefined) {
        const numeric = Number(raw);
        if (Number.isFinite(numeric)) {
          return numeric;
        }
      }
    }
    return fallback;
  };

  return {
    users: {
      total: readMetric('users.total', previousStats?.users?.total ?? 0),
      active: readMetric('users.active', previousStats?.users?.active ?? 0)
    },
    organizations: {
      total: readMetric('organizations.total', previousStats?.organizations?.total ?? 0),
      active: readMetric('organizations.active', previousStats?.organizations?.active ?? 0)
    },
    contents: {
      total: readMetric('contents.total', previousStats?.contents?.total ?? 0),
      approved: readMetric('contents.approved', previousStats?.contents?.approved ?? 0)
    },
    userFlows: {
      total: readMetric('userFlows.total', previousStats?.userFlows?.total ?? 0),
      active: readMetric('userFlows.active', previousStats?.userFlows?.active ?? 0)
    },
    survey: {
      total: readMetric('survey.total', previousStats?.survey?.total ?? 0),
      response: readMetric('survey.response', previousStats?.survey?.response ?? 0)
    },
    visibility: {
      total: readMetric('visibility.total', previousStats?.visibility?.total ?? 0),
      approved: readMetric('visibility.approved', previousStats?.visibility?.approved ?? 0)
    },
    siteCollections: {
      total: readMetric('siteCollections.total', previousStats?.siteCollections?.total ?? 0),
      active: readMetric('siteCollections.active', previousStats?.siteCollections?.active ?? 0)
    },
    sessions: {
      loggedInUsersCurrent: readMetric(
        [
          'sessions.loggedInUsersCurrent',
          'sessions.active',
          'sessions.current',
          'sessions.total',
          'activeSessions',
          'loggedInUsersCurrent',
          'consumer_sessions',
          'ConsumerSessions',
          'sessions'
        ],
        previousStats?.sessions?.loggedInUsersCurrent ?? 0
      )
    },
    apiKeys: {
      provisioned: readMetric('apiKeys.provisioned', previousStats?.apiKeys?.provisioned ?? 0),
      active: readMetric('apiKeys.active', previousStats?.apiKeys?.active ?? 0)
    },
  };
}

// ──────────────────────────────────────────────
// Sub-badge shown below the main number
// ──────────────────────────────────────────────
function SubBadge({ value, label, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-500/10 border-blue-500/20 text-blue-900',
    emerald: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-800',
    indigo: 'bg-indigo-500/10 border-indigo-500/20 text-indigo-900',
    cyan: 'bg-cyan-500/10 border-cyan-500/20 text-cyan-900',
  };
  return (
    <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-bold ${colors[color]}`}>
      <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${color === 'emerald' ? 'bg-emerald-500' : color === 'indigo' ? 'bg-indigo-500' : color === 'cyan' ? 'bg-cyan-500' : 'bg-blue-500'}`} />
      {value} {label}
    </div>
  );
}

// ──────────────────────────────────────────────
// Skeleton card
// ──────────────────────────────────────────────
function SkeletonCard() {
  return (
    <div className="bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-6 shadow-lg border border-blue-100 animate-pulse">
      <div className="flex items-start justify-between mb-4">
        <div className="w-12 h-12 bg-blue-200/40 rounded-2xl" />
        <div className="w-16 h-6 bg-blue-200/30 rounded-full" />
      </div>
      <div className="space-y-2">
        <div className="w-20 h-8 bg-blue-200/40 rounded-lg" />
        <div className="w-28 h-3 bg-blue-200/30 rounded" />
        <div className="w-20 h-5 bg-blue-200/20 rounded-full" />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────
// Stat card
// ──────────────────────────────────────────────
function StatCard({ title, value, icon: Icon, gradient, glowColor, badge, onClick }) {
  const isClickable = typeof onClick === 'function';

  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      className="group relative bg-gradient-to-br from-white to-blue-50/50 rounded-3xl p-6 shadow-lg border border-blue-100
                 hover:shadow-2xl hover:shadow-blue-200/50 hover:border-blue-200 transition-all duration-500 overflow-hidden text-left
                 disabled:opacity-100 disabled:cursor-default"
    >
      {/* hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-indigo-500/5 to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      {/* decorative blobs */}
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-2xl group-hover:scale-125 transition-transform duration-700 pointer-events-none" />
      <div className="absolute -bottom-8 -left-8 w-20 h-20 bg-gradient-to-br from-cyan-400/20 to-blue-500/20 rounded-full blur-xl group-hover:scale-125 transition-transform duration-700 pointer-events-none" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`relative p-3 rounded-2xl bg-gradient-to-br ${gradient} shadow-xl ${glowColor} group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
            <Icon className="w-6 h-6 text-white" strokeWidth={2.5} />
            <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${gradient} blur-xl opacity-50 group-hover:opacity-75 transition-opacity duration-500`} />
          </div>
        </div>

        <div className="space-y-1.5">
          <h3 className="text-3xl font-black bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900 bg-clip-text text-transparent group-hover:scale-105 transition-transform duration-300 origin-left">
            {typeof value === 'number' ? value.toLocaleString() : value}
          </h3>
          <p className="text-sm font-bold text-blue-900/70 uppercase tracking-wider group-hover:text-blue-900 transition-colors">
            {title}
          </p>
          {badge && (
            <div className="pt-1">
              <SubBadge value={badge.value} label={badge.label} color={badge.color} />
            </div>
          )}
        </div>
      </div>

      {/* bottom accent line */}
      <div className={`absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r ${gradient} opacity-60 group-hover:opacity-100 group-hover:h-1 transition-all duration-300`} />
    </button>
  );
}

// ──────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────
export default function DashboardStats({
  orgId,
  token,
  roles = [],
  onNavigate,
  cachedData,
  onDataFetched,
  cacheTimestamp,
}) {
  const [stats, setStats] = useState(defaultStats);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadVersion, setReloadVersion] = useState(0);
  const fetchInProgress = useRef(false);
  const followUpTimeoutRef = useRef(null);
  const hasScheduledFollowUpRef = useRef(false);
  const surveyRecheckCountRef = useRef(0);
  const onDataFetchedRef = useRef(onDataFetched);
  const latestStatsRef = useRef(defaultStats);
  const hasInitializedRef = useRef(false);
  const initialCachedDataRef = useRef(cachedData);
  const initialCacheTimestampRef = useRef(cacheTimestamp);
  useEffect(() => {
    onDataFetchedRef.current = onDataFetched;
  }, [onDataFetched]);

  const parseDashboardSummaryError = useCallback((res) => {
    if (!res) {
      return 'Unable to load dashboard summary metrics.';
    }
    return readAdminApiErrorMessage(res, {
      401: 'Your session is invalid or expired. Please sign in again.',
      403: 'You do not have permission to view dashboard summary metrics.',
      404: 'Dashboard summary metrics were not found.',
      500: 'An error occurred while retrieving dashboard summary metrics.',
      default: 'Unable to load dashboard summary metrics.'
    });
  }, []);

  const fetchDashboardStats = useCallback(async ({ silent = false, scheduleFollowUp = false } = {}) => {
    if (!token || !orgId || fetchInProgress.current) {
      return;
    }

    fetchInProgress.current = true;
    if (!silent) {
      setLoading(true);
    }

    let success = false;
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/summary`, {
        headers: getHeaders(token),
      });

      if (!res || !res.ok) {
        const message = await parseDashboardSummaryError(res);
        throw new Error(message);
      }

      const data = await res.json().catch(() => ({}));
      const metrics = data?.metrics || {};
      const parsed = parseMetrics(metrics, latestStatsRef.current);


      if (!parsed.sessions.loggedInUsersCurrent && parsed.users.active) {
        parsed.sessions.loggedInUsersCurrent = parsed.users.active;
      }

      latestStatsRef.current = parsed;
      setStats(parsed);
      setError('');
      onDataFetchedRef.current?.(parsed);

      const shouldRecheckSurvey = parsed?.survey?.total === 0 && parsed?.survey?.response === 0;
      if (scheduleFollowUp && shouldRecheckSurvey && surveyRecheckCountRef.current < MAX_SURVEY_RECHECKS) {
        surveyRecheckCountRef.current += 1;
      } else if (scheduleFollowUp) {
        hasScheduledFollowUpRef.current = true;
      }
      success = true;
    } catch (err) {
      if (!silent) {
        setError(err?.message || 'Unable to load dashboard summary metrics.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
      fetchInProgress.current = false;

      if (success && scheduleFollowUp && !hasScheduledFollowUpRef.current && surveyRecheckCountRef.current <= MAX_SURVEY_RECHECKS) {
        if (followUpTimeoutRef.current) {
          window.clearTimeout(followUpTimeoutRef.current);
        }
        followUpTimeoutRef.current = window.setTimeout(() => {
          fetchDashboardStats({ silent: true, scheduleFollowUp: true });
        }, FOLLOW_UP_REFRESH_DELAY);
      }
    }
  }, [parseDashboardSummaryError, token, orgId]);

  useEffect(() => {
    if (!token || !orgId) {
      setLoading(false);
      return undefined;
    }

    if (followUpTimeoutRef.current) {
      window.clearTimeout(followUpTimeoutRef.current);
      followUpTimeoutRef.current = null;
    }
    hasScheduledFollowUpRef.current = false;
    surveyRecheckCountRef.current = 0;
    
    if (!hasInitializedRef.current && reloadVersion === 0) {
      hasInitializedRef.current = true;

      const isCacheFresh =
        initialCachedDataRef.current &&
        initialCacheTimestampRef.current &&
        Date.now() - initialCacheTimestampRef.current < CACHE_DURATION;

      if (isCacheFresh) {
        latestStatsRef.current = initialCachedDataRef.current;
        setStats(initialCachedDataRef.current);
        setError('');
        setLoading(false);
        fetchDashboardStats({ silent: true, scheduleFollowUp: true });
        return undefined;
      }
    }

    fetchDashboardStats({ silent: false, scheduleFollowUp: true });
    return undefined;
  }, [fetchDashboardStats, reloadVersion, token, orgId]);

  useEffect(() => {
    return () => {
      if (followUpTimeoutRef.current) {
        window.clearTimeout(followUpTimeoutRef.current);
      }
    };
  }, []);

  const nav = (section, opts) => onNavigate?.(section, opts);
  const normalizedRoles = Array.isArray(roles) ? roles.map((role) => String(role || '').toLowerCase()) : [];
  const isSystemAdmin = normalizedRoles.some((role) => role === 'systemadministrator');
  const canViewSurveyInsights = normalizedRoles.some((role) => (
    role === 'systemadministrator' ||
    role === 'organizationadministrator' ||
    role === 'sitecollectionowner'
  ));

  const cards = [
    {
      title: 'Total Users',
      value: stats.users.total,
      icon: Users,
      gradient: 'from-blue-500 via-blue-600 to-indigo-600',
      glowColor: 'shadow-blue-500/50',
      badge: { value: stats.users.active, label: 'active', color: 'blue' },
      onClick: () => nav('users'),
    },
    {
      title: 'Total Contents',
      value: stats.contents.total,
      icon: FileText,
      gradient: 'from-blue-600 via-blue-700 to-indigo-700',
      glowColor: 'shadow-blue-600/50',
      badge: { value: stats.contents.approved, label: 'approved', color: 'emerald' },
      onClick: () => nav('contents'),
    },
    {
      title: 'Workflows',
      value: stats.userFlows.total,
      icon: Workflow,
      gradient: 'from-cyan-500 via-blue-500 to-indigo-600',
      glowColor: 'shadow-cyan-500/50',
      badge: { value: stats.userFlows.active, label: 'active', color: 'cyan' },
      onClick: () => nav('flows'),
    },
    {
      title: 'Visibility',
      value: stats.visibility.total,
      icon: Eye,
      gradient: 'from-violet-500 via-purple-500 to-indigo-500',
      glowColor: 'shadow-violet-500/50',
      badge: { value: stats.visibility.approved, label: 'approved', color: 'indigo' },
      onClick: () => nav('visibility'),
    },
    {
      title: 'Site Collections',
      value: stats.siteCollections.total,
      icon: Globe,
      gradient: 'from-teal-500 via-cyan-600 to-blue-600',
      glowColor: 'shadow-teal-500/50',
      badge: { value: stats.siteCollections.active, label: 'active', color: 'cyan' },
      onClick: () => nav('sitecollections'),
    },
    {
      title: 'API Keys',
      value: stats.apiKeys.provisioned,
      icon: Key,
      gradient: 'from-amber-500 via-orange-500 to-rose-500',
      glowColor: 'shadow-amber-500/50',
      badge: { value: stats.apiKeys.active, label: 'active', color: 'blue' },
      onClick: () => nav('apikey'),
    },
  ];

  if (isSystemAdmin) {
    cards.splice(2, 0, {
      title: 'Organizations',
      value: stats.organizations.total,
      icon: Building2,
      gradient: 'from-indigo-500 via-indigo-600 to-purple-600',
      glowColor: 'shadow-indigo-500/50',
      badge: { value: stats.organizations.active, label: 'active', color: 'indigo' },
      onClick: () => nav('organization'),
    });
  }



  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Array.from({ length: cards.length || 8 }).map((_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-semibold text-red-800">{error}</p>
            <button
              type="button"
              onClick={() => setReloadVersion((prev) => prev + 1)}
              className="rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-bold text-red-700 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((card, i) => <StatCard key={i} {...card} />)}
      </div>
    </div>
  );
}
